import { IStairCalculationResult, IStairConfig } from '@stair-craft/common/util';

import { EPaneId, IPane, SHEET_SIZE, buildSheet } from '../sheet-layout';
import { dedupeEntities } from './dxf-dedupe';
import { IDxfDocument, serializeDxf } from './dxf-document';
import { IDxfBounds, IDxfEntity, entitiesBounds, isEmptyBounds } from './dxf-entities';
import { dxfLayers } from './dxf-layers';
import { dxfTextMeasurer } from './dxf-measurer';
import { IPlacedView, fitDetailRows, placeViews } from './dxf-model-layout';
import { DXF_PAPER_SIZE, IDxfPaperSheet, paperSheet } from './dxf-sheet';
import { ITitleBlockData, titleBlockData } from './dxf-title-block';

/**
 * The sheet the writer lays out — the one fixed sheet the screen shows too, never the screen's
 * size: the file is a function of the calculation alone, byte for byte.
 */
export const DXF_SHEET_SIZE: { width: number; height: number } = SHEET_SIZE;

/** The extents of an empty drawing: a metre square at the origin, so a reader still has a view to open on. */
const EMPTY_EXTENTS: IDxfBounds = { minX: 0, minY: 0, maxX: 1000, maxY: 1000 };

/** The file as data: the panes laid out in model space and on the sheet, before serialization. */
export function buildDxfDocument(sheetPanes: readonly IPane[], stamp: (denominators: readonly number[]) => ITitleBlockData): IDxfDocument {
    const panes: IPane[] = fitDetailRows(sheetPanes);
    const views: IPlacedView[] = placeViews(panes);
    const model: IDxfEntity[] = dedupeEntities(views.flatMap((view: IPlacedView): IDxfEntity[] => view.entities));
    const scaled: number[] = views
        .filter((view: IPlacedView): boolean => view.pane.scale !== null)
        .map((view: IPlacedView): number => view.denominator);
    const sheet: IDxfPaperSheet = paperSheet(views, panes, stamp(scaled));
    const paper: IDxfEntity[] = dedupeEntities(sheet.entities);
    const used: Set<string> = new Set<string>([...model, ...paper].map((entity: IDxfEntity): string => entity.layer));
    const extents: IDxfBounds = entitiesBounds(model);
    const sheetLabel: string = $localize`:@@dxfStampSheet:Лист`;
    return {
        layers: dxfLayers(
            panes.map((pane: IPane): EPaneId => pane.id),
            used
        ),
        viewports: sheet.viewports,
        modelExtents: isEmptyBounds(extents) ? EMPTY_EXTENTS : extents,
        paperSize: DXF_PAPER_SIZE,
        sheetName: `${sheetLabel} 1`,
        model,
        paper,
    };
}

/**
 * The calculation as a DXF 2007 file in millimetres. Model space holds every view 1 : 1, each on
 * its own group of layers by line kind, its text and dimensions sized for the view's scale; the
 * paper-space sheet «Лист 1» is A2 with the ГОСТ frame, the main inscription and a viewport per
 * view at its standard scale. Labels are in the page language — the same `$localize` calls as
 * the screen sheet. The sheet is laid out with the file's own text sizes (`dxfTextMeasurer`), a
 * deterministic estimate, so the file does not depend on the fonts a browser has. `date` goes into the inscription.
 */
export function writeDxf(result: IStairCalculationResult, config: IStairConfig, date: Date): string {
    const panes: IPane[] = buildSheet(result, config, DXF_SHEET_SIZE, dxfTextMeasurer);
    return serializeDxf(
        buildDxfDocument(panes, (denominators: readonly number[]): ITitleBlockData => titleBlockData(result, config, denominators, date))
    );
}
